import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import {
  Rarity,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import Collapse from '@sorare/core/src/atoms/layout/Collapse';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import useToggle from '@sorare/core/src/hooks/useToggle';

import { PrimaryBuyForAPlayer } from 'components/primaryBuy/PrimaryBuyForAPlayer';
import { BriefSo5AppearanceScore } from 'components/stats/BriefSo5AppearanceScore';
import MatchViewDialog from 'components/stats/MatchViewDialog';
import { PlayerGameScoreStats } from 'components/stats/PlayerGameScoreStats';

import { BriefGame } from '../BriefGame';
import { So5LineupAppearance_so5Appearance } from './__generated__/index.graphql';

type Props = {
  so5Appearance: So5LineupAppearance_so5Appearance;
};

const Root = styled(Vertical).attrs({ gap: 0 })`
  width: 100%;
  border-bottom: 1px solid var(--c-neutral-300);
`;
const AppearanceContent = styled.div`
  padding: var(--double-unit);
  width: 100%;
  cursor: pointer;
`;
const CollapseContent = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
`;

export const So5LineupAppearance = ({ so5Appearance }: Props) => {
  const [expanded, toggleExpanded] = useToggle(false);
  const [openMatchView, setOpenMatchView] = useState(false);

  const { eligiblePlayerGameScores, scoreStatus, anyPlayer, rarity } =
    so5Appearance;

  return (
    <Root>
      <AppearanceContent
        onClick={() => {
          toggleExpanded();
        }}
        role="button"
        tabIndex={0}
        aria-hidden="true"
      >
        <BriefSo5AppearanceScore disableLinks so5Appearance={so5Appearance} />
      </AppearanceContent>
      <Collapse open={expanded} hideGap>
        <CollapseContent>
          {eligiblePlayerGameScores.length === 1 && (
            <BriefGame
              game={eligiblePlayerGameScores[0].anyGame}
              onClick={() => setOpenMatchView(true)}
            />
          )}
          {rarity === Rarity.common && (
            <PrimaryBuyForAPlayer playerSlug={anyPlayer.slug} />
          )}
          {scoreStatus !== ScoreStatus.DID_NOT_PLAY && (
            <PlayerGameScoreStats playerGameScores={eligiblePlayerGameScores} />
          )}
        </CollapseContent>
      </Collapse>
      {!!openMatchView && eligiblePlayerGameScores && (
        <MatchViewDialog
          open
          onClose={() => setOpenMatchView(false)}
          gameId={eligiblePlayerGameScores[0].anyGame.id}
        />
      )}
    </Root>
  );
};

So5LineupAppearance.fragments = {
  so5Appearance: gql`
    fragment So5LineupAppearance_so5Appearance on So5Appearance {
      id
      rarity
      eligiblePlayerGameScores {
        id
        anyGame {
          id
          ...BriefGame_anyGameInterface
        }
        ...PlayerGameScoreStats_playerGameScoreInterface
      }
      scoreStatus
      pictureUrl
      bonus
      ...BriefSo5AppearanceScore_so5Appearance
    }
    ${BriefSo5AppearanceScore.fragments.so5Appearance}
    ${PlayerGameScoreStats.fragments.playerGameScoreInterface}
    ${BriefGame.fragments.anyGameInterface}
  ` as TypedDocumentNode<So5LineupAppearance_so5Appearance>,
};

export default So5LineupAppearance;
